import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import "aos/dist/aos.css";
import SEOHead from "./SEOHead";
import Footer from "./Footer";
import styles from "./AboutUs.module.css";
import visionImg from "../map  image/Vision.png";
import {
  FiMapPin, FiCalendar, FiAward, FiTarget,
  FiCode, FiMap, FiDatabase, FiTrendingUp,
  FiMail, FiArrowRight, FiUsers, FiLayers,
  FiStar, FiCpu, FiGlobe, FiActivity
} from "react-icons/fi";
import {
  SiPython, SiReact, SiPostgresql, SiLeaflet, SiNodedotjs,
  SiBootstrap, SiQgis, SiMicrosoftword
} from "react-icons/si";

/* ─── Radar icon blips - 10 icons representing AK Consultants' work ─── */
const ALL_RADAR_ICONS = [
  { Icon: SiPython,         label: "Python",       color: "#3776AB" },
  { Icon: SiReact,          label: "React",        color: "#61DAFB" },
  { Icon: SiPostgresql,     label: "PostgreSQL",   color: "#0064a5" },
  { Icon: SiLeaflet,        label: "Leaflet/Maps", color: "#199900" },
  { Icon: SiNodedotjs,      label: "Node.js",      color: "#339933" },
  { Icon: SiBootstrap,      label: "Bootstrap",    color: "#7952B3" },
  { Icon: SiQgis,           label: "QGIS",         color: "#589632" },
  { Icon: SiMicrosoftword,  label: "Word",         color: "#2B579A" },
  { Icon: FiCpu,            label: "AI/Automation", color: "#ec4899" },
  { Icon: FiLayers,         label: "Products",     color: "#10b981" },
];

const CYCLE = 3600; // ms per full 360° sweep

/* Pre-generate 10 unique angle+radius positions per rotation cycle */
function genPositions() {
  // Spread 10 icons evenly across 360° with jitter, random radii
  const baseAngles = [18, 54, 90, 126, 162, 198, 234, 270, 306, 342];
  return baseAngles.map((base) => ({
    angle: base + (Math.random() * 28 - 14), // ±14° jitter
    r: 0.30 + Math.random() * 0.52,          // 30%–82% of radius
  }));
}

function Radar() {
  const SIZE = 480;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  const MAX_R = SIZE / 2 - 10;
  const RINGS = 5;

  const [scanDeg, setScanDeg] = useState(0);
  const [blips, setBlips] = useState([]);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  // positions regenerated each rotation
  const positionsRef = useRef(genPositions());
  const lastRotationRef = useRef(-1);
  const firedAnglesRef = useRef(new Set());

  useEffect(() => {
    const loop = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = (ts - startRef.current) % CYCLE;
      const deg = (elapsed / CYCLE) * 360;
      const rotation = Math.floor((ts - startRef.current) / CYCLE);
      setScanDeg(deg);

      // New rotation - regenerate positions and clear fired set
      if (rotation !== lastRotationRef.current) {
        lastRotationRef.current = rotation;
        positionsRef.current = genPositions();
        firedAnglesRef.current = new Set();
      }

      // Fire blip when scan line crosses each icon's angle this rotation
      positionsRef.current.forEach((pos, idx) => {
        const key = `${rotation}-${idx}`;
        if (deg >= pos.angle && deg < pos.angle + 6 && !firedAnglesRef.current.has(key)) {
          firedAnglesRef.current.add(key);
          const rad = ((pos.angle - 90) * Math.PI) / 180;
          const dist = pos.r * MAX_R;
          const px = CX + dist * Math.cos(rad);
          const py = CY + dist * Math.sin(rad);
          // Use % so blips scale correctly when the SVG is scaled down on mobile
          const xPct = (px / SIZE) * 100;
          const yPct = (py / SIZE) * 100;
          const entry = ALL_RADAR_ICONS[idx % ALL_RADAR_ICONS.length];
          const id = `${key}-${ts}`;
          setBlips((prev) => [...prev.slice(-12), { id, Icon: entry.Icon, color: entry.color, xPct, yPct }]);
          setTimeout(() => setBlips((prev) => prev.filter((b) => b.id !== id)), 1800);
        }
      });

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanRad = ((scanDeg - 90) * Math.PI) / 180;
  const sweepEndRad = ((scanDeg - 90 + 60) * Math.PI) / 180;

  return (
    <div className={styles.radarWrap}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className={styles.radarSvg}>
        <defs>
          <radialGradient id="sweepGrad" cx="0%" cy="0%" r="100%">
            <stop offset="0%" stopColor="rgba(249,115,22,0.4)" />
            <stop offset="100%" stopColor="rgba(249,115,22,0.0)" />
          </radialGradient>
          <clipPath id="radarClip">
            <circle cx={CX} cy={CY} r={MAX_R} />
          </clipPath>
        </defs>

        {/* Outer border circle */}
        <circle cx={CX} cy={CY} r={MAX_R} fill="none" stroke="rgba(249,115,22,0.3)" strokeWidth="1.5" />

        {/* Crosshairs */}
        <line x1={CX} y1={10} x2={CX} y2={SIZE-10} stroke="rgba(249,115,22,0.1)" strokeWidth="1"/>
        <line x1={10} y1={CY} x2={SIZE-10} y2={CY} stroke="rgba(249,115,22,0.1)" strokeWidth="1"/>

        {/* Concentric rings */}
        {Array.from({ length: RINGS }).map((_, i) => (
          <circle
            key={i}
            cx={CX} cy={CY}
            r={((i + 1) / RINGS) * MAX_R}
            fill="none"
            stroke="rgba(249,115,22,0.15)"
            strokeWidth="1"
          />
        ))}

        {/* Pulse wave rings - emanate from center */}
        {[0, 1, 2].map((i) => (
          <circle key={`wave-${i}`} cx={CX} cy={CY} r={MAX_R * 0.18} fill="none"
            stroke="rgba(249,115,22,0.55)" strokeWidth="1.5">
            <animate attributeName="r" values={`${MAX_R * 0.18};${MAX_R * 0.92}`}
              dur="3s" begin={`${i * 1}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.6;0"
              dur="3s" begin={`${i * 1}s`} repeatCount="indefinite" />
          </circle>
        ))}

        {/* Sweep wedge */}
        <g clipPath="url(#radarClip)">
          <path
            d={`M ${CX} ${CY}
                L ${CX + MAX_R * Math.cos(sweepEndRad)} ${CY + MAX_R * Math.sin(sweepEndRad)}
                A ${MAX_R} ${MAX_R} 0 0 0
                  ${CX + MAX_R * Math.cos(scanRad)} ${CY + MAX_R * Math.sin(scanRad)}
                Z`}
            fill="url(#sweepGrad)"
          />
        </g>

        {/* Scan line */}
        <line
          x1={CX} y1={CY}
          x2={CX + MAX_R * Math.cos(scanRad)}
          y2={CY + MAX_R * Math.sin(scanRad)}
          stroke="rgba(249,115,22,0.8)"
          strokeWidth="1.5"
        />

        {/* Center dot - pulsing */}
        <circle cx={CX} cy={CY} r={7} fill="#f97316">
          <animate attributeName="r" values="7;11;7" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx={CX} cy={CY} r={3} fill="#fff" opacity="0.9"/>
      </svg>

      {/* Icon blips */}
      {blips.map(({ id, Icon, color, xPct, yPct }) => (
        <div key={id} className={styles.radarBlip} style={{ left: `${xPct}%`, top: `${yPct}%`, '--blip-color': color }}>
          <Icon size={15} />
        </div>
      ))}
    </div>
  );
}

/* ─── Typewriter hook ─── */
// eslint-disable-next-line no-unused-vars
function useTypewriter(text, speed = 28, startDelay = 400) {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // Start only when element enters viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    setDisplayed("");
    let i = 0;
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) clearInterval(interval);
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);
    return () => clearTimeout(timer);
  }, [started, text, speed, startDelay]);

  return { displayed, ref };
}
function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

/* ─── Animated counter card ─── */
function StatCard({ icon: Icon, value, suffix, label, delay, display }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useCountUp(value, 1800, inView);
  return (
    <div ref={ref} className={styles.statCard} data-aos="zoom-in" data-aos-delay={delay}>
      <div className={styles.statIcon}><Icon size={28} /></div>
      <div className={styles.statValue}>{display ?? (count + suffix)}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

/* ─── Tab component ─── */
// eslint-disable-next-line no-unused-vars
function Tabs({ tabs }) {
  const [active, setActive] = useState(0);
  return (
    <div className={styles.tabsWrapper}>
      <div className={styles.tabBar}>
        {tabs.map((t, i) => (
          <button
            key={i}
            className={`${styles.tabBtn} ${active === i ? styles.tabBtnActive : ""}`}
            onClick={() => setActive(i)}
          >
            {t.icon && <t.icon size={16} style={{ marginRight: 6 }} />}
            {t.label}
          </button>
        ))}
      </div>
      <motion.div
        key={active}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className={styles.tabContent}
      >
        {tabs[active].content}
      </motion.div>
    </div>
  );
}

/* ─── Purpose & Goals section ───
   DESKTOP (>1024px): JS scroll-driven stacked cards.
   MOBILE (≤1024px): Pure CSS sticky stacking cards — zero JS scroll listeners.
   Each card uses position:sticky + calculated top offset. The snap container
   uses scroll-snap-type:y proximity so cards click into place on touch.
   will-change:transform on every card triggers GPU compositing.
   Container height uses dvh units to prevent address-bar jump on mobile.
─── */
const CARD_SCROLL_PX = 220;
const MOBILE_BREAKPOINT = 1024;

function PurposeGoalsSection({ goals, leftContent }) {
  const outerRef = useRef(null);
  const sectionRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(-1);
  const activeIdxRef = useRef(-1);
  const [mobileHeaderH, setMobileHeaderH] = useState(160);
  const goalsLen = goals.length;

  const setActive = (idx) => {
    activeIdxRef.current = idx;
    setActiveIdx(idx);
  };

  // ── Measure mobile sticky header height so cards never overlap it ─────
  useEffect(() => {
    if (window.innerWidth > MOBILE_BREAKPOINT) return;
    const el = mobileHeaderRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setMobileHeaderH(el.offsetHeight);
    });
    ro.observe(el);
    setMobileHeaderH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // ── DESKTOP: scroll-position-driven ──────────────────────────────────
  useEffect(() => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) return;
    const onScroll = () => {
      const outer = outerRef.current;
      if (!outer) return;
      const scrolledPast = window.scrollY - outer.offsetTop;
      if (scrolledPast < 0) { setActive(-1); return; }
      const idx = Math.min(Math.floor(scrolledPast / CARD_SCROLL_PX), goalsLen - 1);
      setActive(idx);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [goalsLen]);

  // ── MOBILE/TABLET: pure CSS handles everything — no JS needed ──────────
  // The .purposeMobileOuter layout (rendered below) uses CSS sticky stacking
  // cards with scroll-snap. No scroll listeners, no window.scrollTo calls.

  const totalHeight = `calc(100vh + ${goalsLen * CARD_SCROLL_PX}px)`;

  return (
    <>
      {/* ── DESKTOP layout: tall outer div + JS-driven sticky wrapper ── */}
      <div ref={outerRef} style={{ height: totalHeight, position: 'relative' }} className={styles.purposeDesktopOuter}>
        <div className={styles.purposeStickyWrap} ref={sectionRef}>
          <section className={styles.purposeSection}>
            <div className={styles.container}>
              <div className={styles.purposeHeader}>
                <span className={styles.sectionTag}>Why We Exist</span>
                <h2 className={styles.purposeTitle}>Purpose &amp; Goals</h2>
                <p className={styles.purposeSubtitle}>
                  We bridge the gap between complex challenges and elegant solutions - democratizing access to information and tools that matter.
                </p>
              </div>
              <div className={styles.purposeGrid}>
                <div className={styles.purposeLeft}>{leftContent}</div>
                <div className={styles.goalsSticky}>
                  <div className={styles.goalsStack}>
                    {goals.map((g, i) => {
                      const isActive = i === activeIdx;
                      const isSettled = i < activeIdx;
                      return (
                        <div
                          key={i}
                          className={styles.goalCard}
                          style={{
                            transform: i <= activeIdx ? g.tilt : `${g.tilt} translateY(108%)`,
                            opacity: i <= activeIdx ? 1 : 0,
                            zIndex: i + 1,
                            transition: 'transform 0.55s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease, background 0.5s ease, border-color 0.5s ease',
                            background: isActive ? '#ffffff' : isSettled ? '#ffffff' : 'rgba(249,115,22,0.08)',
                            borderColor: isActive ? '#f97316' : isSettled ? '#e2e8f0' : 'rgba(249,115,22,0.25)',
                          }}
                        >
                          <div className={styles.goalCardHeader}>
                            <div className={styles.goalIcon}><g.icon size={22} /></div>
                            <h4 className={styles.goalTitle}>{g.title}</h4>
                          </div>
                          <p className={styles.goalDesc}>{g.desc}</p>
                          <p className={styles.goalDetail}>{g.detail}</p>
                          <span className={styles.goalCardNum}>
                            {String(i + 1).padStart(2, '0')} / {String(goals.length).padStart(2, '0')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.scrollHint} style={{ opacity: activeIdx >= goals.length - 1 ? 0 : 1, transition: 'opacity 0.3s' }}>
                    <span>{activeIdx < 0 ? 'scroll to start' : 'scroll to reveal'}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 3v10M4 9l4 4 4-4" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* ── MOBILE layout: pure CSS sticky stacking cards (≤1024px only) ── */}
      <div className={styles.purposeMobileOuter}>

        {/* Compact sticky header — tag + title + subtitle only.
            Stays pinned below the nav. Cards stack below this, never behind it. */}
        <div className={styles.purposeMobileHeader} ref={mobileHeaderRef}>
          <div className={styles.container}>
            <span className={styles.sectionTag}>Why We Exist</span>
            <h2 className={styles.purposeTitle}>Purpose &amp; Goals</h2>
            <p className={styles.purposeSubtitle}>
              We bridge the gap between complex challenges and elegant solutions - democratizing access to information and tools that matter.
            </p>
          </div>
        </div>

        {/* Cards — top offset = nav height + measured sticky header height + i * peek */}
        <div
          className={styles.mobileCardsContainer}
          style={{ '--header-h': `${mobileHeaderH}px` }}
        >
          {goals.map((g, i) => (
            <div
              key={i}
              className={styles.mobileCardWrap}
              style={{ '--i': i }}
            >
              <div className={styles.mobileGoalCard}>
                <div className={styles.goalCardHeader}>
                  <div className={styles.goalIcon}><g.icon size={22} /></div>
                  <h4 className={styles.goalTitle}>{g.title}</h4>
                </div>
                <p className={styles.goalDesc}>{g.desc}</p>
                <p className={styles.goalDetail}>{g.detail}</p>
                <span className={styles.goalCardNum}>
                  {String(i + 1).padStart(2, '0')} / {String(goals.length).padStart(2, '0')}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Left content — paragraphs, quote, pills — normal flow below cards */}
        <div className={styles.purposeMobileLeft}>
          <div className={styles.container}>{leftContent}</div>
        </div>

      </div>
    </>
  );
}
const DESC = "We don't just deliver technology - we solve the problems that matter. From making democracy more transparent to precision GIS mapping, every solution we build is rooted in purpose, built for scale, and designed to create lasting impact.";

function InnovationStrip() {
  const stripRef = useRef(null);
  const bgRef = useRef(null);
  const [fgVisible, setFgVisible] = useState(false);
  const fgRef = useRef(null);

  // Scroll-driven: bg text rises up (parallax) + fades in; reverses on scroll up
  useEffect(() => {
    const bg = bgRef.current;
    // On mobile the strip is shorter and text smaller - apply bolder weight once
    if (bg && window.innerWidth <= 768) {
      bg.style.fontWeight = '1250';
      bg.style.webkitTextStroke = '0.5px rgba(255,255,255,0.15)';
    }

    const update = () => {
      const strip = stripRef.current;
      if (!strip || !bg) return;
      const rect = strip.getBoundingClientRect();
      const winH = window.innerHeight;
      // progress: 0 when strip bottom just enters viewport, 1 when strip top hits viewport top
      const progress = Math.min(Math.max((winH - rect.top) / (rect.height + winH * 0.3), 0), 1);
      // On mobile: travel only 40px so white top stays visible; desktop: 120px
      const travel = window.innerWidth <= 768 ? 40 : 120;
      const ty = travel - progress * travel;
      const op = 0.08 + progress * 0.77;
      bg.style.opacity = op;
      bg.style.transform = `translateX(-50%) translateY(${ty}px)`;
    };
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, []);

  // Fg fade-up - reversible (shows/hides based on intersection)
  useEffect(() => {
    const el = fgRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setFgVisible(entry.isIntersecting),
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className={styles.innovationStrip} ref={stripRef}>
      {/* Giant bg text */}
      <div className={styles.innovationBgText} ref={bgRef} aria-hidden="true">
        Innovation That Matters
      </div>

      {/* Radial spotlight behind fg text */}
      <div className={styles.innovationSpotlight} />

      {/* Foreground - fade-up on scroll */}
      <div
        ref={fgRef}
        className={`${styles.innovationFg} ${fgVisible ? styles.innovationFgVisible : ""}`}
      >
        <h2 className={styles.innovationHeading}>Why Ace Keystone?</h2>
        <p className={styles.innovationDesc}>{DESC}</p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ */
const AboutUs = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  /* ── Timeline data ── */
  // eslint-disable-next-line no-unused-vars
  const timeline = [
    { year: "2022", title: "The Beginning", desc: "Founded in Coimbatore with a vision to democratize technology and solve real-world problems." },
    { year: "2023", title: "Building Urimai", desc: "Spent over a year scraping and cleaning thousands of political affidavits to serve Tamil Nadu's 6 crore+ voters." },
    { year: "2024", title: "GIS Expansion", desc: "Launched precision geospatial mapping services for governments and infrastructure planners worldwide." },
    { year: "2025", title: "Incorporated as Pvt. Ltd.", desc: "Formalized as a Private Limited company - cementing our foundation for scalable, long-term growth and institutional partnerships." },
  ];

  /* ── Goal cards - 6 items with unique tilts ── */
  const goals = [
    { icon: FiTrendingUp, title: "Empower Decisions", tilt: "rotate(-2deg)",
      desc: "Build technology that empowers decision-making at individual and organizational levels - from voters to enterprise leaders. We turn raw complexity into clear, actionable insight.",
      detail: "We believe the right data, presented clearly, changes outcomes. Whether it's a citizen checking a politician's record or a planner routing infrastructure - clarity drives better choices. Every interface we design is built around the person who needs to act on it." },
    { icon: FiLayers, title: "Scalable Solutions", tilt: "rotate(1.5deg)",
      desc: "Create sustainable, scalable solutions that grow with our clients' evolving needs without compromising on quality or performance. Built right the first time, so you never have to rebuild.",
      detail: "Our architecture decisions are made with tomorrow in mind. We build systems that handle 100 users today and 10 million tomorrow - without a rewrite. Scalability isn't an afterthought; it's baked into every layer from day one." },
    { icon: FiUsers, title: "Trusted Partners", tilt: "rotate(-1deg)",
      desc: "Establish ourselves as trusted long-term partners in every digital transformation journey our clients undertake. We show up beyond the deadline, not just before it.",
      detail: "We don't disappear after delivery. We stay involved, iterate based on feedback, and treat every client's problem as our own - because long-term trust is the only metric that matters. Relationships outlast any single project." },
    { icon: FiTarget, title: "Real Impact", tilt: "rotate(2deg)",
      desc: "Transform how people interact with critical information in their daily lives - making complexity simple and accessible. Impact is the only measure of success we care about.",
      detail: "Impact isn't measured in lines of code. It's measured in the voter who finally understands their candidate, or the engineer who gets accurate terrain data on day one. We build for the end user, always - not just the spec." },
    { icon: FiGlobe, title: "Convergence Engineering", tilt: "rotate(-1.5deg)",
      desc: "We work at the convergence of electronics and embedded systems, sensing and instrumentation, artificial intelligence, advanced analytics, and digital systems engineering.",
      detail: "This convergence enables organisations to progress from conceptual innovation to field‑ready, operationally robust technologies — bridging the gap between research-grade ideas and systems that perform reliably in the real world." },
    { icon: FiCpu, title: "Intelligence by Design", tilt: "rotate(1deg)",
      desc: "Our practice is built on hardware‑aware engineering — integrating electronic design, sensor systems, embedded intelligence, and AI‑driven algorithms within a unified architecture.",
      detail: "Intelligence is designed into the system, not layered on top — delivering solutions that are technically rigorous, reliable in real operating environments, and scalable across applications." },
  ];

  /* ── Team roles ── */
  // eslint-disable-next-line no-unused-vars
  const teamRoles = [
    {
      icon: FiCode,
      title: "Software Developers",
      desc: "We write code that ships, scales, and survives. Our developers work across the full stack — from React frontends to Node.js APIs to PostgreSQL schemas — with a bias toward clean architecture and long-term maintainability. No over-engineering, no shortcuts.",
    },
    {
      icon: FiMap,
      title: "GIS Specialists",
      desc: "Our geospatial team turns raw coordinates into decisions. From satellite image processing and terrain modelling to property parcel mapping and highway planning — we work with QGIS, Leaflet, and custom pipelines to deliver precision spatial intelligence for governments and infrastructure teams.",
    },
    {
      icon: FiTarget,
      title: "Product Strategists",
      desc: "We don't build features — we solve problems. Our strategists sit at the intersection of user research, market reality, and technical feasibility. They define what gets built, why it matters, and how it creates value — before a single line of code is written.",
    },
    {
      icon: FiDatabase,
      title: "Data Analysts",
      desc: "Behind every product we ship is a layer of structured, validated data. Our analysts clean, model, and interpret complex datasets — from political affidavit records to geospatial attribute tables — turning noise into the kind of clarity that drives real decisions.",
    },
    {
      icon: FiCpu,
      title: "Embedded & Electronics Engineers",
      desc: "Our hardware team designs and builds the intelligence that lives inside devices. Embedded engineers, electronics engineers, and design engineers work together on circuit design, firmware, and PCB layouts — building the low-level systems that power our autonomous sensing solutions from the ground up.",
    },
    {
      icon: FiActivity,
      title: "Medical Electronics & Sensing",
      desc: "We develop autonomous sensing systems that operate at the edge — from medical electronics that monitor and respond in real time, to sensor arrays built for precision environments. Our team bridges biomedical engineering and embedded design to create hardware that doesn't just collect data, it acts on it.",
    },
  ];

  /* ── Products tab content ── */
  // eslint-disable-next-line no-unused-vars
  const productCards = [
    {
      tag: "Live",
      tagColor: "#22c55e",
      title: "Urimai",
      subtitle: "Political Transparency Platform",
      desc: "Making political affidavits digestible and searchable for 6 crore+ voters in Tamil Nadu.",
      icon: FiStar,
    },
    {
      tag: "Coming Soon",
      tagColor: "#f59e0b",
      title: "Course Curator",
      subtitle: "Educational Intelligence System",
      desc: "Intelligent course recommendations, fusion learning paths, and custom curriculum tools.",
      icon: FiLayers,
    },
    {
      tag: "In Dev",
      tagColor: "#6366f1",
      title: "Tender Automation",
      subtitle: "Enterprise Process Tool",
      desc: "Streamlining complex tender and procurement workflows for enterprise clients.",
      icon: FiTrendingUp,
    },
  ];

  // eslint-disable-next-line no-unused-vars
  const gisCards = [
    { icon: FiMap, title: "Base & Topographic Mapping", desc: "Detailed maps across all scales for urban planning and infrastructure." },
    { icon: FiMapPin, title: "Property & Parcel Mapping", desc: "Accurate land ownership delineation for legal compliance." },
    { icon: FiTrendingUp, title: "Highway & Transit Mapping", desc: "Transportation infrastructure planning and management." },
    { icon: FiDatabase, title: "Terrain Modelling", desc: "Advanced 3D modeling for engineering and planning applications." },
    { icon: FiLayers, title: "Satellite Image Processing", desc: "Enhanced imagery for maximum accuracy and usability." },
    { icon: FiTarget, title: "Agro-Forestry Mapping", desc: "Environmental monitoring and resource management solutions." },
  ];

  /* ── Testimonials ── */
  // eslint-disable-next-line no-unused-vars
  const testimonials = [
    {
      quote: "Their geospatial mapping solutions provided the precision and clarity we needed for our smart city project.",
      author: "Municipal Planning Authority",
      location: "Tamil Nadu",
    },
    {
      quote: "Their work on our infrastructure mapping project exceeded expectations and delivered exactly what we needed.",
      author: "Infrastructure Development Corporation",
      location: "",
    },
    {
      quote: "They don't just deliver products; they partner with you to ensure the solution truly addresses your needs.",
      author: "Regional Utility Board",
      location: "",
    },
  ];

  return (
    <>
      <SEOHead
        title="About Us | AK Consultants"
        description="Ace Keystone Consultants - a product development and GIS services company founded in 2022, building scalable solutions that create real-world impact."
        image="https://www.ak-consultants.in/static/media/about-us.eeddcd253edfca07c2a0.jpg"
      />

      {/* ══ DARK BAND - hero + innovation strip share one seamless BG ══ */}
      <div className={styles.darkBand}>

      {/* ══ HERO - split layout ══ */}
      <section className={styles.hero}>
        <div className={styles.heroOverlay} />

        {/* LEFT - text content */}
        <motion.div
          className={styles.heroLeft}
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        >
          <span className={styles.heroEyebrow}>Coimbatore · Est. 2022</span>
          <h1 className={styles.heroHeading}>
            Built to Solve.<br />Designed to Scale.<br />
            <span className={styles.heroAccent}>Made to Last.</span>
          </h1>
          <p className={styles.heroSub}>
We are a deep‑technology and advanced systems consultancy specialising in the design, modelling, and deployment of intelligent cyber‑physical systems, where electronics and artificial intelligence are engineered together to solve complex, real‑world problems.          </p>
          <p className={styles.heroSub2}>
            A focused team of developers, geospatial specialists, and product thinkers working at the intersection of technology and purpose.
          </p>
          <div className={styles.heroCtas}>
            <a href="/contact" className={styles.ctaPrimary}>
              Talk to Us <FiArrowRight style={{ marginLeft: 6 }} />
            </a>
            <Link to="/services" className={styles.ctaSecondary}>
              Explore Services
            </Link>
          </div>
        </motion.div>

        {/* RIGHT - radar */}
        <motion.div
          className={styles.heroRight}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
          aria-hidden="true"
        >
          <Radar />
        </motion.div>
      </section>

      {/* ══ INNOVATION STRIP ══ */}
      <InnovationStrip />

      </div>{/* end darkBand */}

      {/* ══ SNAPSHOT CARDS ══ */}
      <section className={styles.snapshotSection}>
        <div className={styles.container}>
          <div className={styles.snapshotGrid}>
            <div className={styles.snapCard} data-aos="fade-up" data-aos-delay="0">
              <FiCalendar size={32} className={styles.snapIcon} />
              <div className={styles.snapValue}>2022</div>
              <div className={styles.snapLabel}>Founded</div>
            </div>
            <div className={styles.snapCard} data-aos="fade-up" data-aos-delay="100">
              <FiTrendingUp size={32} className={styles.snapIcon} />
              <div className={styles.snapValue}>100%</div>
              <div className={styles.snapLabel}>Bootstrapped & Profitable</div>
            </div>
            <div className={styles.snapCard} data-aos="fade-up" data-aos-delay="200">
              <FiMapPin size={32} className={styles.snapIcon} />
              <div className={styles.snapValue}>CBE</div>
              <div className={styles.snapLabel}>Coimbatore HQ</div>
            </div>
            <div className={styles.snapCard} data-aos="fade-up" data-aos-delay="300">
              <FiTarget size={32} className={styles.snapIcon} />
              <div className={styles.snapValue}>2x</div>
              <div className={styles.snapLabel}>Core Focus Areas</div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ VISION BANNER ══ */}
      <div className={styles.visionBanner}>
        <h3 className={styles.visionBannerHeading} data-aos="fade-up" data-aos-delay="0">Signed MOU Partner</h3>
        <img
          src={visionImg}
          alt="Our Vision"
          className={styles.visionBannerImg}
          data-aos="fade-up"
          data-aos-delay="150"
        />
      </div>

      {/* ══ PURPOSE & GOALS ══ */}
      <PurposeGoalsSection
        goals={goals}
        leftContent={
          <>
            <p className={styles.purposeDesc} data-aos="fade-up" data-aos-delay="0">
              Every product we build starts with a single question: <em>"How can we make this better, simpler, and more accessible for the people who need it most?"</em>
            </p>
            <p className={styles.purposeDesc} data-aos="fade-up" data-aos-delay="100">
              From making democracy more transparent through Urimai, to delivering precision GIS mapping for infrastructure planners - our work is rooted in purpose, not just profit.
            </p>
            <blockquote className={styles.purposeQuote} data-aos="fade-up" data-aos-delay="200">
              "Technology should serve people, not the other way around."
            </blockquote>
            <div className={styles.purposePillars} data-aos="fade-up" data-aos-delay="300">
              {["Transparency", "Precision", "Scale", "Impact"].map((p) => (
                <span key={p} className={styles.purposePill}>{p}</span>
              ))}
            </div>
          </>
        }
      />

      {/* ══ INTEGRATED DELIVERY MODEL ══ */}
      <section className={styles.deliverySection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader} data-aos="fade-up">
            <span className={styles.sectionTagLight}>How We Work</span>
            <h2 className={styles.sectionTitleLight}>Integrated Delivery Model</h2>
          </div>
          <div className={styles.deliveryGrid}>
            <div className={styles.deliveryMain} data-aos="fade-up" data-aos-delay="0">
              <p className={styles.deliveryLead}>
                AK Consultants adopts an end‑to‑end integrated delivery model that brings together electronics engineering, artificial intelligence, and cyber security to design and deploy secure, intelligent, and resilient cyber‑physical systems.
              </p>
              <p className={styles.deliveryBody}>
                Our approach ensures that hardware, intelligence, and security are co‑engineered from the outset, rather than treated as isolated or sequential components. Our multidisciplinary teams work across the full system stack — integrating electronic hardware design, sensing and instrumentation, RF and embedded systems, firmware development, edge and cloud‑based AI, and secure data architectures within a unified engineering framework.
              </p>
              <p className={styles.deliveryBody}>
                We incorporate secure‑by‑design principles, covering device‑level security, secure firmware, protected communication interfaces, data integrity, access control, and resilient system architectures. This ensures that intelligent systems remain trustworthy, robust, and compliant when deployed in operational, safety‑critical, or hostile environments.
              </p>
              <p className={styles.deliveryBody}>
                AK Consultants delivers solutions that move from system architecture and technology strategy through prototype development, validation, deployment, and lifecycle management. The result is innovation that is not only intelligent and high‑performance, but also secure, reliable, and engineered for long‑term real‑world operation.
              </p>
            </div>
            <div className={styles.deliveryPillars} data-aos="fade-up" data-aos-delay="100">
              {[
                { icon: FiCpu,        label: "Electronics Engineering",   desc: "Hardware design, sensing, RF, embedded systems & firmware." },
                { icon: FiActivity,   label: "Artificial Intelligence",   desc: "Edge & cloud AI co‑engineered with the hardware layer." },
                { icon: FiLayers,     label: "Cyber Security",            desc: "Secure‑by‑design from device to data architecture." },
                { icon: FiGlobe,      label: "Lifecycle Management",      desc: "Architecture → prototype → validation → deployment." },
              ].map((p, i) => (
                <div key={i} className={styles.deliveryPillar} data-aos="fade-up" data-aos-delay={i * 80}>
                  <div className={styles.deliveryPillarIcon}><p.icon size={22} /></div>
                  <div>
                    <h4 className={styles.deliveryPillarLabel}>{p.label}</h4>
                    <p className={styles.deliveryPillarDesc}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ ACHIEVEMENTS ══ */}
      <section className={styles.achievementsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader} data-aos="fade-up">
            <span className={styles.sectionTag}>Impact</span>
            <h2 className={styles.sectionTitle}>By the Numbers</h2>
          </div>
          <div className={styles.statsGrid}>
            <StatCard icon={FiUsers} value={567} suffix="" display="5.67 Cr+" label="Voter Outreach Initiative" delay="0" />
            <StatCard icon={FiMap} value={10} suffix="+" label="GIS Projects Delivered" delay="100" />
            <StatCard icon={FiCode} value={8} suffix="+" label="Products in Pipeline" delay="200" />
            <StatCard icon={FiAward} value={3} suffix="+" label="Years of Innovation" delay="300" />
          </div>
        </div>
      </section>



      {/* ══ CTA ══ */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaOverlay} />
        <div className={styles.ctaContent} data-aos="zoom-in">
          <h2 className={styles.ctaTitle}>Ready to build something that matters?</h2>
          <p className={styles.ctaSub}>
            Whether it's software, GIS, or digital transformation - let's start a conversation.
          </p>
          <div className={styles.ctaBtns}>
            <a href="/contact" className={styles.ctaPrimary}>
              <FiMail style={{ marginRight: 8 }} /> Get in Touch
            </a>
            <a href="/our-products" className={styles.ctaSecondary}>
              Explore Products <FiArrowRight style={{ marginLeft: 6 }} />
            </a>
          </div>
        </div>
      </section>

      {showScrollTop && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          <i className="fa fa-arrow-up" />
        </button>
      )}

      <div className={styles.footerWrap}>
        <Footer />
      </div>
    </>
  );
};

export default AboutUs;
